import React, { useState } from "react";

import { Stack } from "@mui/material";

import { HookResult } from "@fartherfinance/frontend/api/Types";

import { taxStatusIdToLabel } from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioBuilder/constants";
import { TaxTypeEnum } from "@src/multiCustodian/components/Advisor/Investments/ModelMarketplaceV2/PortfolioBuilder/reducer/Types";
import { UNKNOWN_VALUE } from "@src/multiCustodian/pages/Advisor/common";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import ItemWithExpandCollapseContent, {
  LoadingItemWithExpandCollapseContent,
} from "@src/sharedComponents/ItemWithExpandCollapseContent/ItemWithExpandCollapseContent";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./Details.module.css";

interface CustomModelDetails {
  modelName: string;
  taxType: TaxTypeEnum;
}

interface Data {
  modelSeries?: string;
  assetAllocation?: string;
  provider?: string;
  tags: string;
  name: string;
  customModelDetails?: CustomModelDetails;
}

type Props = HookResult<Data>;

const PortfolioDetailsSideBarDetailsSection: React.FC<Props> = (props) => {
  const [expandDetails, setExpandDetails] = useState<boolean>(true);

  if (props.hasError) {
    return <ErrorDetails />;
  }

  if (props.isLoading) {
    return <LoadingDetails />;
  }

  return (
    <ItemWithExpandCollapseContent
      label={"Details"}
      expand={expandDetails}
      setExpand={setExpandDetails}
    >
      {props.data.customModelDetails !== undefined ? (
        <>
          <TextRow
            textStart={"Asset Management Firm"}
            textEnd={"Custom Model"}
          />

          <TextRow
            textStart={"Name"}
            textEnd={props.data.customModelDetails.modelName}
          />

          <TextRow
            textStart={"Tax Status"}
            textEnd={taxStatusIdToLabel[props.data.customModelDetails.taxType]}
            hideBottomBorder={true}
          />
        </>
      ) : (
        <>
          <TextRow
            textStart={"Asset Management Firm"}
            textEnd={props.data.provider}
          />

          <TextRow textStart={"Name"} textEnd={props.data.name} />

          <TextRow textStart={"Series"} textEnd={props.data.modelSeries} />

          <TextRow
            textStart={"Asset Allocation"}
            textEnd={props.data.assetAllocation}
          />

          <TextRow
            textStart={"Tax Status"}
            textEnd={props.data.tags}
            hideBottomBorder={true}
          />
        </>
      )}
    </ItemWithExpandCollapseContent>
  );
};

const TextRow = ({
  textStart,
  textEnd,
  hideBottomBorder,
}: {
  textStart: React.ReactNode;
  textEnd?: React.ReactNode;
  hideBottomBorder?: boolean;
}) => {
  return (
    <Stack
      direction={"row"}
      className={toClassName(styles.container, {
        [styles.noBottomBorder]: hideBottomBorder,
      })}
    >
      <Text variant={"body"} color={"text"}>
        {textStart}
      </Text>

      {textEnd !== undefined ? (
        <Text variant={"body"} color={"text"} className={styles.textEnd}>
          {textEnd}
        </Text>
      ) : (
        <Text variant={"body"} color={"textSubtle"}>
          {UNKNOWN_VALUE}
        </Text>
      )}
    </Stack>
  );
};

const NUM_ROWS = 5;
const LoadingDetails = () => {
  return (
    <LoadingItemWithExpandCollapseContent>
      {new Array(NUM_ROWS).fill(null).map((_, i) => (
        <TextRow
          key={i}
          textStart={<Skeleton width={80} />}
          textEnd={<Skeleton width={120} />}
          hideBottomBorder={NUM_ROWS === i + 1}
        />
      ))}
    </LoadingItemWithExpandCollapseContent>
  );
};

const ErrorDetails = () => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <ItemWithExpandCollapseContent
      label={"Error getting details"}
      expand={isOpen}
      setExpand={setIsOpen}
    >
      <Text
        style={{
          height: 180,
          textAlign: "center",
          alignContent: "center",
        }}
      >
        Try again later
      </Text>
    </ItemWithExpandCollapseContent>
  );
};

export default PortfolioDetailsSideBarDetailsSection;
